import React, { useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import AuthContext from "./auth/Context";
import moment from 'moment';
import CircularProgress from '@mui/material/CircularProgress';
import Badge from '@mui/material/Badge';
import * as utils from "../utils"
import {numeral} from "../utils"
import { counterQueue } from "./counterQueue"; // <<< GLOBAL QUEUE (concurrency=5)

const { EmitEvents } = utils;

import { useQuery } from '@tanstack/react-query';
import { listinfoHeaders } from './listinfoBinding';

/**
 * @component CounterWidget
 * @description Hiển thị số lượng bản ghi từ một API, với cơ chế cache thông minh
 * và hàng đợi có giới hạn concurrency — tránh gọi API đồng loạt khi dùng nhiều widget cùng lúc.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚡ QUICK START
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * // 1. Hiển thị số đơn giản nhất
 * <CounterWidget code="orders" />
 * // → Render: <span>1,234</span>
 *
 * // 2. Hiển thị với badge (dùng trong menu, icon)
 * <CounterWidget code="notifications" badge>
 *   <BellIcon />
 * </CounterWidget>
 * // → Render: <Badge badgeContent={5}><BellIcon /></Badge>
 *
 * // 3. Render tùy biến hoàn toàn
 * <CounterWidget code="orders" render={(count) => (
 *   <h1 style={{ color: count > 100 ? 'red' : 'green' }}>{count} đơn hàng</h1>
 * )} />
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 📦 PROPS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * @prop {string}   code             [BẮT BUỘC] Mã định danh listinfo, dùng để resolve API endpoint.
 *
 * @prop {object}   condition        Điều kiện lọc gửi lên API. Hỗ trợ mọi field của collection.
 *                                   Lưu ý: idApp và id_app sẽ tự động bị loại bỏ trước khi gửi.
 *                                   @default {}
 *                                   @example { status: 'active', type: 'VIP' }
 *
 * @prop {string}   fields           Danh sách field cần lấy, chỉ dùng khi có handleData.
 *                                   @example "name,email,status"
 *
 * @prop {function} handleData       Hàm xử lý dữ liệu thủ công. Khi truyền vào, widget sẽ load
 *                                   toàn bộ data về rồi đếm qua hàm này thay vì đếm từ DB.
 *                                   Dùng khi cần logic đếm phức tạp phía client.
 *                                   @example handleData={(res) => res.data.filter(x => x.paid).length}
 *
 * @prop {function} render           Render prop — nhận count, trả về JSX tùy biến hoàn toàn.
 *                                   Ưu tiên cao hơn badge và children.
 *                                   @example render={(count) => <Tag>{count}</Tag>}
 *
 * @prop {boolean}  badge            Nếu true, bọc children trong MUI Badge với badgeContent=count.
 *                                   @default false
 *
 * @prop {string}   variant          Variant của MUI Badge (chỉ dùng khi badge=true).
 *                                   @example "dot"
 *
 * @prop {any}      children         Nội dung bên trong (thường dùng kèm badge=true).
 *
 * @prop {object}   style            Style cho thẻ <span> hiển thị số (khi không dùng render/badge).
 *
 * @prop {function} renderRunning    Hàm render trạng thái đang loading. Thay thế spinner mặc định.
 *                                   @example renderRunning={() => <Skeleton width={40} />}
 *
 * @prop {object}   styleRunning     Style cho CircularProgress spinner mặc định khi đang loading.
 *
 * @prop {function} onChange         Callback nhận giá trị count mỗi khi số thay đổi.
 *                                   @example onChange={(count) => setTotalOrders(count)}
 *
 * @prop {number}   refreshTrigger   Tăng giá trị này để trigger refetch thủ công từ component cha.
 *                                   @example const [tick, setTick] = useState(0)
 *                                            <button onClick={() => setTick(t => t+1)}>Làm mới</button>
 *                                            <CounterWidget code="orders" refreshTrigger={tick} />
 *
 * @prop {number}   queueConcurrency Số widget chạy song song trong hàng đợi toàn cục.
 *                                   Áp dụng cho toàn bộ app, không chỉ widget hiện tại.
 *                                   @default 5
 *                                   @example 1  → tuần tự hoàn toàn
 *                                            5  → 5 cái song song (mặc định)
 *                                            10 → 10 cái song song (mạng tốt)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔄 TỰ ĐỘNG REFRESH
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Widget tự động refresh theo 3 cách:
 *   1. Định kỳ ngẫu nhiên 5–10 phút (tránh tất cả widget refresh cùng lúc)
 *   2. Khi nhận EmitEvent: `${code}:new`, `${code}:update`, `${code}:delete`
 *      → Dùng khi tạo/sửa/xóa bản ghi ở nơi khác trong app
 *      → EmitEvents.emit(`orders:new`)  // CounterWidget code="orders" sẽ tự refetch
 *   3. Khi refreshTrigger thay đổi (xem prop bên trên)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🧪 VÍ DỤ THỰC TẾ
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * // Đếm đơn hàng chờ xử lý, hiển thị badge trên icon menu
 * <CounterWidget code="orders" condition={{ status: 'pending' }} badge>
 *   <ShoppingCartIcon />
 * </CounterWidget>
 *
 * // Đếm và thông báo ra ngoài khi số thay đổi
 * <CounterWidget
 *   code="tickets"
 *   condition={{ assigned_to: userId, resolved: false }}
 *   onChange={(count) => dispatch(setUnresolvedCount(count))}
 * />
 *
 * // Đếm phức tạp phía client (ví dụ: lọc theo điều kiện không có trong DB query)
 * <CounterWidget
 *   code="products"
 *   fields="price,discount"
 *   handleData={(res) => res.data.filter(p => p.price - p.discount < 100).length}
 *   render={(count) => <span>{count} sản phẩm dưới 100k</span>}
 * />
 *
 * // Giới hạn 3 widget chạy song song (mạng yếu)
 * <CounterWidget code="reports" queueConcurrency={3} />
 */
export default function CounterWidget(props) {
  const {
    code,
    // Mã listinfo mượn hành vi server (xem listinfoBinding.js). Không truyền -> lùi về `code`.
    listinfoCode,
    condition = {},
    fields,
    renderRunning,
    styleRunning,
    style,
    onChange,
    handleData,
    badge,
    children,
    variant,
    render,
    refreshTrigger,
    queueConcurrency, // <<< MỚI: override concurrency cho queue (optional)
  } = props;

  const context = useContext(AuthContext);

  // AbortController để hủy task trong queue khi component unmount
  const abortControllerRef = useRef(null);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Cho phép component cha override concurrency của queue tại runtime
  // Ví dụ: <CounterWidget queueConcurrency={10} ... />
  useEffect(() => {
    if (typeof queueConcurrency === 'number' && queueConcurrency > 0) {
      counterQueue.setConcurrency(queueConcurrency);
    }
  }, [queueConcurrency]);

  const idApp = context.appInfo?._id || condition.idApp || condition.id_app;
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const autoRefreshInterval = useMemo(() => {
    const minMs = 5 * 60 * 1000;
    const maxMs = 10 * 60 * 1000;
    return Math.floor(Math.random() * (maxMs - minMs + 1) + minMs);
  }, []);

  // ================= QUERY 1: RESOLVE API CODE =================
  const { data: countConfig, isLoading: isResolvingApi } = useQuery({
    queryKey: ['counter_resolve_api_code', code],
    queryFn: () =>
      counterQueue.enqueue(async () => {
        const res = await context.apis.asyncGetList(
          userToken,
          "listinfo",
          { condition: { code }, limit: 1, fields: "api_code,code,options" }
        );
        const listInfo = res?.data?.[0] || res?.[0];

        if (listInfo) {
          listInfo.options = listInfo.options || {};
          listInfo.options.props = listInfo.options.props || "{}";

          if (typeof listInfo.options.props === "string") {
            try {
              let body_func = listInfo.options.props;
              if (body_func.indexOf("return") < 0) body_func = `return ${body_func}`;
              body_func = `return (async ()=>{ ${body_func} })()`;
              const func = new Function("Moment", "API", "utils", "context", "listInfo", body_func);
              listInfo.options.props = await func(moment, context.apis, utils, context, listInfo);
            } catch (e) {
              console.error("props of listinfo is not valid", e, listInfo?.options?.props);
            }
          }
        }

        const resolvedApiCode = listInfo?.api_code || listInfo?.code;
        return { resolvedApiCode, listInfo };
      }, abortControllerRef.current?.signal),

    enabled: !!code && !!userToken,
    staleTime: Infinity,
  });

  // ================= QUERY 2: ĐẾM DỮ LIỆU =================
  const { data: number = 0, isLoading, refetch } = useQuery({
    queryKey: [idApp, code, 'counter', { condition, fields, hasHandleData: !!handleData }],

    queryFn: () =>
      // Tối đa `concurrency` tasks chạy song song (mặc định 5)
      // Ví dụ 100 widgets: batch [1-5] → batch [6-10] → ... → batch [96-100]
      counterQueue.enqueue(async () => {
        const cleanCondition = {
          ...condition,
          ...(countConfig?.listInfo?.options?.props?.requiredCondition || {})
        };
        delete cleanCondition.idApp;
        delete cleanCondition.id_app;

        const options = {
          condition: cleanCondition,
          id_app: idApp,
          headers: listinfoHeaders({ listinfoCode, moduleCode: code })
        };

        if (handleData) {
          options.fields = fields;
          options.limit = 1000000000000;
          const res = await context.apis.asyncGetList(userToken, countConfig.resolvedApiCode, options);
          return handleData(res);
        } else {
          options.count = true;
          const res = await context.apis.asyncGetList(userToken, countConfig.resolvedApiCode, options);
          return res?.rows_number || 0;
        }
      }, abortControllerRef.current?.signal),

    enabled: !!countConfig?.resolvedApiCode && !!idApp && !!userToken,
    staleTime: autoRefreshInterval - 1000,
    refetchInterval: autoRefreshInterval,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
  });

  // ================= onChange =================
  useEffect(() => {
    if (onChange && number !== undefined) onChange(number);
  }, [number, onChange]);

  // ================= refreshTrigger =================
  useEffect(() => {
    if (refreshTrigger > 0) refetch();
  }, [refreshTrigger, refetch]);

  // ================= EmitEvents =================
  useEffect(() => {
    const handleEvent = () => refetch();

    EmitEvents.subscribe(`local-${code}:update`, handleEvent);
    EmitEvents.subscribe(`${code}:update`, handleEvent);
    EmitEvents.subscribe(`local-${code}:new`, handleEvent);
    EmitEvents.subscribe(`${code}:new`, handleEvent);
    EmitEvents.subscribe(`local-${code}:delete`, handleEvent);
    EmitEvents.subscribe(`${code}:delete`, handleEvent);

    return () => {
      EmitEvents.unsubscribe(`local-${code}:update`, handleEvent);
      EmitEvents.unsubscribe(`${code}:update`, handleEvent);
      EmitEvents.unsubscribe(`local-${code}:new`, handleEvent);
      EmitEvents.unsubscribe(`${code}:new`, handleEvent);
      EmitEvents.unsubscribe(`local-${code}:delete`, handleEvent);
      EmitEvents.unsubscribe(`${code}:delete`, handleEvent);
    };
  }, [code, refetch]);

  // ================= RENDER =================
  const isLoadingOpts = isLoading || isResolvingApi;

  if (isLoadingOpts) {
    if (renderRunning) return renderRunning();
    return (
      <>
        {children}
        <CircularProgress
          style={{ width: 16, height: 16, position: "absolute", right: "50%", top: "50%", ...(styleRunning || {}) }}
        />
      </>
    );
  }

  if (render) return render(number);

  if (badge) {
    return (
      <Badge badgeContent={number} color="error" variant={variant}>
        {children || (number ? numeral(number).format() : "--")}
      </Badge>
    );
  }

  return (
    <>
      {children}
      <span style={style}>
        {number ? numeral(number).format() : "0"}
      </span>
    </>
  );
}

CounterWidget.propTypes = {
  condition:        PropTypes.any,
  fields:           PropTypes.any,
  code:             PropTypes.string.isRequired,
  renderRunning:    PropTypes.any,
  styleRunning:     PropTypes.any,
  style:            PropTypes.any,
  onChange:         PropTypes.func,
  handleData:       PropTypes.func,
  badge:            PropTypes.bool,
  children:         PropTypes.any,
  variant:          PropTypes.string,
  render:           PropTypes.func,
  conditionRerender:PropTypes.any,
  queueConcurrency: PropTypes.number, // <<< MỚI: số task chạy song song (mặc định 5)
};